import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useContractABI } from "@/hooks/useContractABI";
import { useColorMode } from "@chakra-ui/react";
import { highlight, languages } from "prismjs";
import "prismjs/components/prism-json";
import * as React from "react";
import Editor from "react-simple-code-editor";
interface FunctionAbiSelectorProps {
  label: string;
  description?: string;
  headerRight?: React.ReactNode;
  value: any; // The selected function ABI
  onChange: (value: any) => void;
  chainId: any;
  contractAddress: any;
  onFunctionNameChange?: (functionName: string) => void; // Callback to update functionName field
  onModeChange?: (isManual: boolean) => void; // Callback when switching between auto/manual mode
  preloadedAbi?: string; // Pre-loaded ABI JSON string (from contract project), skips chain fetch
  includeWriteFunctions?: boolean; // Show write functions (nonpayable/payable) in addition to view/pure
}
export default function FunctionAbiSelector({
  label,
  description,
  headerRight,
  value,
  onChange,
  chainId,
  contractAddress,
  onFunctionNameChange,
  onModeChange,
  preloadedAbi,
  includeWriteFunctions = false
}: FunctionAbiSelectorProps) {
  const [isManuallyEdited, setIsManuallyEdited] = React.useState(false);
  const {
    colorMode
  } = useColorMode();

  // Notify parent when mode changes
  React.useEffect(() => {
    if (onModeChange) {
      onModeChange(isManuallyEdited);
    }
  }, [isManuallyEdited, onModeChange]);
  const hasChainId = chainId && chainId !== "";
  const hasContractAddress = contractAddress && contractAddress !== "";
  const hasPreloadedAbi = !!preloadedAbi;
  const shouldFetchFromChain = hasChainId && hasContractAddress && !hasPreloadedAbi;
  const shouldShow = hasPreloadedAbi || hasChainId && hasContractAddress;
  const {
    abi: fetchedAbi,
    isLoading,
    status
  } = useContractABI({
    chainId,
    address: contractAddress,
    enabled: shouldFetchFromChain
  });

  // Use preloaded ABI if available, otherwise use fetched ABI
  const abi = React.useMemo(() => {
    if (preloadedAbi) {
      try {
        let parsed = JSON.parse(preloadedAbi);
        // Handle double-encoded JSON strings (backend stores ABI as string)
        if (typeof parsed === "string") {
          parsed = JSON.parse(parsed);
        }
        return Array.isArray(parsed) ? parsed : null;
      } catch {
        return null;
      }
    }
    return fetchedAbi;
  }, [preloadedAbi, fetchedAbi]);

  // In manual mode, pre-populate with full ABI if available and field is empty
  React.useEffect(() => {
    if (isManuallyEdited && abi && (!value || value === "[]" || value === "")) {
      // Pre-populate manual textarea with full ABI
      onChange(JSON.stringify(abi, null, 2));
    }
  }, [isManuallyEdited, abi, value, onChange]);

  // Filter functions based on mode
  const readFunctions = React.useMemo(() => {
    if (!abi) return [];
    return abi.filter(item => {
      if (item.type !== "function" || !item.name) return false;
      if (includeWriteFunctions) {
        // Show all functions (read + write)
        return true;
      }
      // Show only view/pure functions
      return item.stateMutability === "view" || item.stateMutability === "pure";
    });
  }, [abi, includeWriteFunctions]);

  // Find selected function
  const selectedFunction = React.useMemo(() => {
    if (!value) return null;
    try {
      const parsed_0 = typeof value === "string" ? JSON.parse(value) : value;
      return parsed_0;
    } catch {
      return null;
    }
  }, [value]);
  const handleSelectFunction = (fn: any) => {
    const functionAbi = JSON.stringify(fn);
    onChange(functionAbi);
    setIsManuallyEdited(false); // Ensure we're in auto mode after selection
    if (onFunctionNameChange && fn.name) {
      onFunctionNameChange(fn.name);
    }
  };
  const handleManualChange = (newValue: string) => {
    onChange(newValue);
    setIsManuallyEdited(true);
  };

  // Hide completely until prerequisites are met
  if (!shouldShow) {
    return null;
  }
  return <div className="space-y-2">
      {label && <div className="flex items-center justify-between">
          <label className="field-label block">{label}</label>

          {/* Auto/Manual Tabs - Always show */}
          <div className="flex items-center gap-3">
            <div className="border-ds-border-default bg-ds-surface-card inline-flex rounded-lg border p-0.5">
              <button type="button" onClick={() => setIsManuallyEdited(false)} className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${!isManuallyEdited ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300" : "text-ds-text-subtle hover:text-ds-text-primary dark:text-ds-text-disabled dark:hover:text-zinc-100"}`}>
                Auto
              </button>
              <button type="button" onClick={() => setIsManuallyEdited(true)} className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${isManuallyEdited ? "bg-ds-neutral-200 text-ds-text-primary dark:bg-ds-neutral-700 dark:text-zinc-100" : "text-ds-text-subtle hover:text-ds-text-primary dark:text-ds-text-disabled dark:hover:text-zinc-100"}`}>
                Manual
              </button>
            </div>
            {headerRight}
          </div>
        </div>}

      {/* Only show description in manual mode */}
      {description && isManuallyEdited && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}

      {/* Loading state - only when fetching from chain */}
      {isLoading && !isManuallyEdited && !hasPreloadedAbi && <p className="text-xs text-blue-600 dark:text-blue-400">Fetching contract functions...</p>}

      {/* Error/Not Found state - only when fetching from chain */}
      {!isManuallyEdited && !hasPreloadedAbi && status === "not-found" && <p className="text-xs text-amber-600 dark:text-amber-400">Contract not verified. Please enter ABI manually.</p>}
      {!isManuallyEdited && !hasPreloadedAbi && status === "error" && <p className="micro-label text-red-600 dark:text-red-400">Failed to fetch ABI. Please try manual mode.</p>}

      {/* Auto Mode: Display available functions */}
      {!isManuallyEdited && readFunctions.length > 0 && <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className={`text-sm font-medium ${selectedFunction ? "text-blue-800 dark:text-blue-200" : "text-red-500 dark:text-red-500"}`}>
              {readFunctions.length} {includeWriteFunctions ? "Function" : "View Function"}
              {readFunctions.length !== 1 ? "s" : ""} Available - Select One:
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-2">
            {readFunctions.map((fn_0, index) => {
          const isSelected = selectedFunction?.name === fn_0.name;
          return <AccordionItem key={index} value={`function-${index}`} className={`rounded-lg border ${isSelected ? "border-blue-400 bg-blue-100 dark:border-blue-600 dark:bg-blue-900" : "border-blue-200 bg-white dark:border-blue-700 dark:bg-blue-950"}`}>
                  <div className="relative">
                    <AccordionTrigger className="px-4 pr-24 hover:no-underline">
                      <div className="flex w-full min-w-0 items-center gap-3 pr-4">
                        {isSelected && <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500 text-white dark:bg-blue-600">
                            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>}
                        <div className="min-w-0 text-left">
                          <p className="truncate font-mono text-sm font-semibold text-blue-900 dark:text-blue-100">
                            {fn_0.name}
                          </p>
                          <p className="text-xs text-blue-700 dark:text-blue-300">
                            {fn_0.inputs?.length || 0} parameter{(fn_0.inputs?.length || 0) !== 1 ? "s" : ""} →{" "}
                            {fn_0.outputs?.length || 0} output{(fn_0.outputs?.length || 0) !== 1 ? "s" : ""}
                          </p>
                        </div>
                      </div>
                    </AccordionTrigger>
                    {!isSelected && <Button type="button" variant="brand" size="xs" className="absolute right-10 top-1/2 shrink-0 -translate-y-1/2" onClick={e => {
                e.stopPropagation();
                handleSelectFunction(fn_0);
              }}>
                        Select
                      </Button>}
                  </div>
                  <AccordionContent className="px-4">
                    <div className="space-y-3 pt-2">
                      {/* Inputs */}
                      {fn_0.inputs && fn_0.inputs.length > 0 && <div className="space-y-2">
                          <p className="text-xs font-medium text-blue-800 dark:text-blue-200">Inputs:</p>
                          <div className="space-y-1">
                            {fn_0.inputs.map((input: any, idx: number) => <div key={idx} className="flex items-center gap-2 rounded bg-blue-50 px-2 py-1 text-xs dark:bg-blue-900/50">
                                <span className="font-mono font-medium text-blue-900 dark:text-blue-100">
                                  {input.name || `arg${idx}`}
                                </span>
                                <span className="text-blue-700 dark:text-blue-300">{input.type}</span>
                              </div>)}
                          </div>
                        </div>}

                      {/* Outputs */}
                      {fn_0.outputs && fn_0.outputs.length > 0 && <div className="space-y-2">
                          <p className="text-xs font-medium text-blue-800 dark:text-blue-200">Outputs:</p>
                          <div className="space-y-1">
                            {fn_0.outputs.map((output: any, idx_0: number) => <div key={idx_0} className="flex items-center gap-2 rounded bg-blue-50 px-2 py-1 text-xs dark:bg-blue-900/50">
                                <span className="font-mono font-medium text-blue-900 dark:text-blue-100">
                                  {output.name || `return${idx_0}`}
                                </span>
                                <span className="text-blue-700 dark:text-blue-300">{output.type}</span>
                              </div>)}
                          </div>
                        </div>}

                      {/* Raw JSON */}
                      <details className="text-xs">
                        <summary className="cursor-pointer text-blue-700 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200">
                          View raw JSON
                        </summary>
                        <pre className="text-2xs mt-2 overflow-x-auto rounded bg-blue-100 p-2 font-mono text-blue-900 dark:bg-blue-900 dark:text-blue-100">
                          {JSON.stringify(fn_0, null, 2)}
                        </pre>
                      </details>
                    </div>
                  </AccordionContent>
                </AccordionItem>;
        })}
          </Accordion>
        </div>}

      {/* Show selected function if we have one but no available functions */}
      {!isManuallyEdited && !isLoading && readFunctions.length === 0 && selectedFunction && <div className="space-y-3">
          <div>
            <h4 className="font-mono text-sm font-semibold text-blue-900 dark:text-blue-100">
              {selectedFunction.name}
            </h4>
            <p className="mt-1 text-xs text-blue-700 dark:text-blue-300">
              {selectedFunction.inputs?.length || 0} input{(selectedFunction.inputs?.length || 0) !== 1 ? "s" : ""}
            </p>
          </div>

          <details className="text-xs">
            <summary className="cursor-pointer text-blue-700 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200">
              View raw JSON
            </summary>
            <pre className="text-2xs mt-2 overflow-x-auto rounded bg-blue-100 p-2 font-mono text-blue-900 dark:bg-blue-900 dark:text-blue-100">
              {JSON.stringify(selectedFunction, null, 2)}
            </pre>
          </details>
        </div>}

      {/* Manual Mode: Code editor with syntax highlighting */}
      {isManuallyEdited && <>
          <div className="border-ds-neutral-300 dark:border-ds-neutral-700 bg-ds-surface-card rounded-lg border focus-within:border-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500/20">
            <Editor value={value || ""} onValueChange={handleManualChange} highlight={code => highlight(code, languages.json, "json")} placeholder="[]" padding={12} style={{
          fontFamily: '"Fira Code", "Fira Mono", Consolas, Monaco, "Courier New", monospace',
          fontSize: 14,
          lineHeight: 1.6,
          minHeight: "250px",
          outline: "none"
        }} preClassName="text-ds-text-primary dark:text-zinc-200" textareaClassName="caret-zinc-900 dark:caret-white" className={colorMode === "dark" ? "prism-dark-theme" : "prism-light-theme"} />
          </div>
          <style jsx global>{`
            /* Light theme colors - unified with WorkflowJsonView */
            .prism-light-theme {
              color: #18181b;
            }
            .prism-light-theme .token.comment,
            .prism-light-theme .token.prolog,
            .prism-light-theme .token.doctype,
            .prism-light-theme .token.cdata {
              color: #a1a1aa;
            }
            .prism-light-theme .token.punctuation {
              color: #71717a;
            }
            .prism-light-theme .token.property,
            .prism-light-theme .token.tag,
            .prism-light-theme .token.symbol {
              color: #a855f7;
            }
            .prism-light-theme .token.boolean,
            .prism-light-theme .token.number,
            .prism-light-theme .token.constant {
              color: #ea580c;
            }
            .prism-light-theme .token.deleted {
              color: #dc2626;
            }
            .prism-light-theme .token.selector,
            .prism-light-theme .token.string,
            .prism-light-theme .token.char,
            .prism-light-theme .token.builtin,
            .prism-light-theme .token.inserted {
              color: #059669;
            }
            .prism-light-theme .token.attr-name,
            .prism-light-theme .token.function,
            .prism-light-theme .token.class-name {
              color: #d97706;
            }
            .prism-light-theme .token.operator,
            .prism-light-theme .token.entity,
            .prism-light-theme .token.url,
            .prism-light-theme .token.atrule,
            .prism-light-theme .token.keyword {
              color: #2563eb;
            }
            .prism-light-theme .token.attr-value {
              color: #059669;
            }

            /* Dark theme colors - unified with WorkflowJsonView */
            .prism-dark-theme {
              color: #e4e4e7;
            }
            .prism-dark-theme .token.comment,
            .prism-dark-theme .token.prolog,
            .prism-dark-theme .token.doctype,
            .prism-dark-theme .token.cdata {
              color: #71717a;
            }
            .prism-dark-theme .token.punctuation {
              color: #a1a1aa;
            }
            .prism-dark-theme .token.property,
            .prism-dark-theme .token.tag,
            .prism-dark-theme .token.symbol {
              color: #e879f9;
            }
            .prism-dark-theme .token.boolean,
            .prism-dark-theme .token.number,
            .prism-dark-theme .token.constant {
              color: #fb923c;
            }
            .prism-dark-theme .token.deleted {
              color: #ef4444;
            }
            .prism-dark-theme .token.selector,
            .prism-dark-theme .token.string,
            .prism-dark-theme .token.char,
            .prism-dark-theme .token.builtin,
            .prism-dark-theme .token.inserted {
              color: #34d399;
            }
            .prism-dark-theme .token.attr-name,
            .prism-dark-theme .token.function,
            .prism-dark-theme .token.class-name {
              color: #fbbf24;
            }
            .prism-dark-theme .token.operator,
            .prism-dark-theme .token.entity,
            .prism-dark-theme .token.url,
            .prism-dark-theme .token.atrule,
            .prism-dark-theme .token.keyword {
              color: #60a5fa;
            }
            .prism-dark-theme .token.attr-value {
              color: #34d399;
            }
          `}</style>
        </>}
    </div>;
}